import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Search, Check } from "lucide-react";

export default function NouvelleCarte() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [cardType, setCardType] = useState("");
  const [collection, setCollection] = useState("all");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => { api.get("/cartes/meta").then(({ data }) => setMeta(data)).catch(() => toast.error("Erreur de chargement.")); }, []);

  const templates = useMemo(() => {
    if (!meta) return [];
    return meta.templates.filter((t) => {
      if (cardType && !t.card_types.includes(cardType)) return false;
      if (collection !== "all" && t.collection !== collection) return false;
      if (q && !t.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [meta, cardType, collection, q]);

  const choose = async (templateId) => {
    setCreating(true);
    try {
      const { data } = await api.post("/cartes", { card_type: cardType, template_id: templateId });
      toast.success("Carte créée ! À vous de la personnaliser.");
      nav(`/ma-carte/${data.id}/editer`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la création."); setCreating(false); }
  };

  if (!meta) return <div className="text-slate-400 py-16 text-center">Chargement…</div>;

  /* Étape 1 : choisir le type */
  if (!cardType) {
    return (
      <div data-testid="nouvelle-carte-type">
        <Button variant="ghost" size="sm" onClick={() => nav("/ma-carte")} className="mb-3 text-slate-500"><ArrowLeft size={16} className="mr-1" /> Retour</Button>
        <h1 className="font-heading text-2xl md:text-3xl font-bold">Quel type de carte souhaitez-vous créer&nbsp;?</h1>
        <p className="text-slate-500 text-sm mt-1 mb-6">Chaque profil dispose de modèles et de champs adaptés.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(meta.card_types).map(([key, v]) => (
            <button key={key} onClick={() => setCardType(key)} data-testid={`type-${key}`}
              className="text-left p-4 rounded-2xl border border-slate-200 bg-white hover:border-[#E05638] hover:shadow-md transition-all">
              <div className="font-bold text-slate-900">{v.label}</div>
              <div className="text-xs text-slate-400 mt-1">Modèles & sections adaptés</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  /* Étape 2 : choisir un modèle */
  return (
    <div data-testid="nouvelle-carte-gallery">
      <Button variant="ghost" size="sm" onClick={() => setCardType("")} className="mb-3 text-slate-500"><ArrowLeft size={16} className="mr-1" /> Changer de type</Button>
      <h1 className="font-heading text-2xl font-bold mb-1">Choisissez un modèle</h1>
      <p className="text-slate-500 text-sm mb-4">{meta.card_types[cardType]?.label} · {templates.length} modèle(s)</p>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="pl-8 w-48" data-testid="tpl-search" />
        </div>
        <button onClick={() => setCollection("all")} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${collection === "all" ? "bg-[#E05638] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>Toutes</button>
        {Object.entries(meta.collections).map(([key, label]) => (
          <button key={key} onClick={() => setCollection(key)} data-testid={`coll-${key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${collection === key ? "bg-[#E05638] text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{label}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="tpl-grid">
        {templates.map((t) => (
          <button key={t.id} disabled={creating} onClick={() => choose(t.id)} data-testid={`tpl-${t.id}`}
            className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-lg hover:-translate-y-0.5 transition-all text-left disabled:opacity-50">
            <TemplatePreview t={t} />
            <div className="p-2.5">
              <div className="font-semibold text-sm text-slate-900 truncate">{t.name}</div>
              <div className="text-[11px] text-slate-400">{meta.collections[t.collection]}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* Mini aperçu statique du modèle (couleurs + typo + structure) */
function TemplatePreview({ t }) {
  const c = t.colors || {};
  const f = t.fonts || {};
  return (
    <div className="h-40 relative overflow-hidden" style={{ background: c.bg }}>
      <div className="absolute inset-x-0 top-0 h-16" style={{ background: `linear-gradient(135deg, ${c.primary}, ${c.secondary})` }} />
      <div className="absolute left-1/2 -translate-x-1/2 top-9 w-12 h-12 rounded-full border-2 border-white shadow" style={{ background: c.surface }} />
      <div className="absolute inset-x-0 top-[88px] text-center px-2">
        <div className="mx-auto h-2.5 w-20 rounded" style={{ background: c.title, fontFamily: f.title }} />
        <div className="mx-auto mt-1.5 h-1.5 w-14 rounded" style={{ background: c.primary }} />
        <div className="mx-auto mt-2 h-4 w-24 rounded-md" style={{ background: c.button }} />
      </div>
    </div>
  );
}
